<template>
  <div class="sku-detail-page">
    <div class="header">
      <button @click="goBack" class="back-btn">← 返回 SKU 列表</button>
      <h2>SKU 详情</h2>
      <button @click="openEdit" class="edit-btn" v-if="sku.id">编辑</button>
    </div>

    <div v-if="loading" class="loading-text">加载中...</div>

    <template v-else-if="!sku.id">
      <div class="empty-text">未找到该 SKU，请返回列表重试。</div>
    </template>

    <template v-else>
      <!-- 基本信息 -->
      <div class="info-card">
        <div class="card-header">
          <h3>基本信息</h3>
        </div>
        <div class="card-body">
          <div class="detail-top">
            <img :src="sku.image || DEFAULT_NO_IMAGE" class="detail-img" />
            <div class="info-grid">
              <div class="info-item">
                <label>SKU ID</label>
                <span>{{ sku.id }}</span>
              </div>
              <div class="info-item">
                <label>店家自定义货号</label>
                <span>{{ sku.code || '-' }}</span>
              </div>
              <div class="info-item">
                <label>计量单位</label>
                <span>{{ sku.unit || '-' }}</span>
              </div>
              <div class="info-item">
                <label>售价</label>
                <span>{{ formatYuan(sku.sale_price) }}</span>
              </div>
              <div class="info-item">
                <label>每单位搬运费</label>
                <span>{{ formatYuan(sku.carry_fee) }}</span>
              </div>
              <div class="info-item">
                <label>步梯每层楼层费</label>
                <span>{{ formatYuan(sku.floor_fee) }}</span>
              </div>
              <div class="info-item">
                <label>累计销量</label>
                <span>{{ sku.sales_count || 0 }}</span>
              </div>
              <div class="info-item">
                <label>累计代送</label>
                <span>{{ sku.delivery_count || 0 }}</span>
              </div>
              <div class="info-item">
                <label>是否默认 SKU</label>
                <span :class="'status ' + (sku.is_default === 1 ? 'is-default' : '')">
                  {{ sku.is_default === 1 ? '是' : '否' }}
                </span>
              </div>
              <div class="info-item">
                <label>状态</label>
                <span :class="['status', sku.status === 1 ? 'is-on' : 'is-off']">
                  {{ sku.status === 1 ? '上架' : '下架' }}
                </span>
              </div>
              <div class="info-item">
                <label>创建时间</label>
                <span>{{ formatDate(sku.created_at) }}</span>
              </div>
              <div class="info-item">
                <label>更新时间</label>
                <span>{{ formatDate(sku.updated_at) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 规格信息 -->
      <div class="info-card spec-card">
        <div class="card-header">
          <h3>规格信息</h3>
        </div>
        <div class="card-body">
          <div v-if="specList.length" class="spec-list">
            <div v-for="item in specList" :key="item.spec_id" class="spec-row">
              <label class="spec-name">{{ item.spec_name }}</label>
              <span class="spec-value">{{ item.value_name }}</span>
            </div>
          </div>
          <div v-else class="empty-text">该 SKU 未配置规格</div>
        </div>
      </div>

      <!-- 所属物品 -->
      <div class="info-card product-card">
        <div class="card-header">
          <h3>所属物品</h3>
        </div>
        <div class="card-body">
          <div class="info-grid">
            <div class="info-item">
              <label>物品名称</label>
              <span>{{ product.name || '-' }}</span>
            </div>
            <div class="info-item">
              <label>物品分类</label>
              <span>{{ product.category ? product.category.name : '未分类' }}</span>
            </div>
            <div class="info-item">
              <label>所属店家</label>
              <span>{{ shopName }}</span>
            </div>
            <div class="info-item">
              <label>物品状态</label>
              <span :class="['status', product.status === 1 ? 'is-on' : 'is-off']">
                {{ product.status === 1 ? '上架' : '下架' }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- 编辑弹窗 -->
    <div v-if="showEditModal" class="modal-overlay" @click.self="closeEdit">
      <div class="modal-content">
        <div class="modal-header">
          <h3>编辑 SKU</h3>
          <button @click="closeEdit" class="close-btn">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>规格（按规格维度逐项选择）</label>
            <SkuSpecSelector
              :product-id="productId"
              v-model="editForm.specValueIds"
              :sku-id="skuId"
              :key="'edit-' + productSpecVersion + '-' + skuId"
            />
          </div>
          <div class="form-group">
            <label>店家自定义货号</label>
            <input v-model="editForm.code" type="text" class="form-control" placeholder="货号 / 外部编码" />
          </div>
          <div class="form-group">
            <ImageUpload
              ref="skuImageUpload"
              :value="editForm.image || DEFAULT_NO_IMAGE"
              :lazy="true"
              category="product"
              label="SKU 图片（建议规格：375x375）"
              @input="val => editForm.image = val"
            />
          </div>
          <div class="form-group">
            <label>计量单位</label>
            <input v-model="editForm.unit" type="text" class="form-control" placeholder="如：件、个、斤、箱" />
          </div>
          <div class="form-row">
            <div class="form-group form-group-half">
              <label>售价（元）</label>
              <input v-model.number="salePriceYuan" type="number" class="form-control" min="0" step="0.01" placeholder="单位为元" />
            </div>
            <div class="form-group form-group-half">
              <label>每单位搬运费（元）</label>
              <input v-model.number="carryFeeYuan" type="number" class="form-control" min="0" step="0.01" placeholder="单位为元" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group form-group-half">
              <label>步梯每层楼层费（元）</label>
              <input v-model.number="floorFeeYuan" type="number" class="form-control" min="0" step="0.01" placeholder="单位为元" />
            </div>
            <div class="form-group form-group-half">
              <label>累计销量</label>
              <input v-model.number="editForm.sales_count" type="number" class="form-control" min="0" disabled />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group form-group-half">
              <label>是否默认 SKU</label>
              <select v-model.number="editForm.is_default" class="form-control">
                <option :value="0">否</option>
                <option :value="1">是</option>
              </select>
            </div>
            <div class="form-group form-group-half">
              <label>状态</label>
              <select v-model.number="editForm.status" class="form-control">
                <option :value="1">上架</option>
                <option :value="0">下架</option>
              </select>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeEdit" class="btn-cancel">取消</button>
          <button @click="saveEdit" class="btn-save">保存</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getProductDetail, getSkuDetail, updateSku } from '@/api/products'
import { getSpecValueOptions, getSpecOptions, getSkuSpecs, deleteSkuSpec, createSkuSpec } from '@/api/specs'
import SkuSpecSelector from '@/components/product/SkuSpecSelector.vue'
import { DEFAULT_NO_IMAGE } from '@/constants'
import ImageUpload from '@/components/common/Form/ImageUpload.vue'

function buildEmptyForm() {
  return {
    code: '',
    image: '',
    unit: '',
    specValueIds: [],
    sale_price: 0,
    carry_fee: 100,
    floor_fee: 10,
    sales_count: 0,
    status: 1,
    is_default: 0
  }
}

export default {
  name: 'SkuDetail',
  components: { SkuSpecSelector, ImageUpload },
  data() {
    return {
      skuId: null,
      productId: null,
      product: {},
      sku: {},
      specValueMap: {},
      specNameMap: {},
      loading: true,
      autoEdit: false,
      showEditModal: false,
      productSpecVersion: 0,
      editForm: buildEmptyForm()
    }
  },
  computed: {
    DEFAULT_NO_IMAGE() {
      return DEFAULT_NO_IMAGE
    },
    // 将 SKU 已选规格值 ID 转换为 [{ spec_id, spec_name, value_name }] 用于展示
    specList() {
      const valueIds = this.sku._specValueIds || []
      return valueIds.map(id => {
        const v = this.specValueMap[id]
        const valueName = (v && v.name) || '-'
        const specName = (v && v.spec_id != null && this.specNameMap[v.spec_id]) || '规格'
        return { spec_id: (v && v.spec_id) || '', spec_name: specName, value_name: valueName }
      })
    },
    shopName() {
      if (this.sku.shop_name && this.sku.shop_name !== '-') {
        return this.sku.shop_name
      }
      if (!this.product.shop_id) return '-'
      if (this.product.shop && this.product.shop.name) {
        return (this.product.shop.community ? this.product.shop.community.name + ' - ' : '') + this.product.shop.name
      }
      return '店家#' + this.product.shop_id
    },
    salePriceYuan: {
      get() {
        return this.editForm.sale_price / 100
      },
      set(val) {
        this.editForm.sale_price = Math.round(Number(val || 0) * 100)
      }
    },
    carryFeeYuan: {
      get() {
        return this.editForm.carry_fee / 100
      },
      set(val) {
        this.editForm.carry_fee = Math.round(Number(val || 0) * 100)
      }
    },
    floorFeeYuan: {
      get() {
        return this.editForm.floor_fee / 100
      },
      set(val) {
        this.editForm.floor_fee = Math.round(Number(val || 0) * 100)
      }
    }
  },
  mounted() {
    this.skuId = parseInt(this.$route.params.id, 10)
    this.productId = parseInt(this.$route.query.productId, 10)
    this.autoEdit = this.$route.query.edit === '1'
    this.loadSpecMaps()
    this.loadProduct()
    this.loadSku()
  },
  methods: {
    goBack() {
      this.$router.push({ name: 'productSku', query: { productId: this.productId } })
    },
    async loadProduct() {
      if (!this.productId) return
      try {
        const response = await getProductDetail(this.productId)
        if (response && response.data) {
          this.product = response.data
        }
      } catch (error) {
      }
    },
    async loadSku() {
      if (!this.productId) {
        this.loading = false
        return
      }
      try {
        const response = await getSkuDetail(this.productId, this.skuId)
        if (response && response.data) {
          this.sku = response.data
          const skuSpecs = this.sku.sku_specs || []
          this.$set(this.sku, '_specValueIds', skuSpecs.map(s => s.spec_value_id).filter(Boolean))
          if (this.autoEdit) {
            this.autoEdit = false
            this.openEdit()
          }
        }
      } catch (error) {
      } finally {
        this.loading = false
      }
    },
    async loadSpecMaps() {
      try {
        const svRes = await getSpecValueOptions()
        const list = (svRes && svRes.data && svRes.data.spec_values) || []
        const map = {}
        list.forEach(v => { map[v.id] = v })
        this.specValueMap = map
        const spRes = await getSpecOptions()
        const specs = (spRes && spRes.data && spRes.data.specs) || []
        const specNameMap = {}
        specs.forEach(s => { if (s && s.id != null) specNameMap[s.id] = s.name })
        this.specNameMap = specNameMap
      } catch (e) {
        this.specValueMap = {}
        this.specNameMap = {}
      }
    },
    openEdit() {
      const sku = this.sku
      const form = buildEmptyForm()
      form.code = sku.code || ''
      form.image = sku.image || ''
      form.unit = sku.unit || ''
      form.specValueIds = []
      form.sale_price = sku.sale_price || 0
      form.carry_fee = sku.carry_fee || 0
      form.floor_fee = sku.floor_fee || 0
      form.sales_count = sku.sales_count || 0
      form.status = sku.status !== undefined ? sku.status : 1
      form.is_default = sku.is_default !== undefined ? sku.is_default : 0
      this.editForm = form
      this.showEditModal = true
    },
    closeEdit() {
      this.showEditModal = false
      this.editForm = buildEmptyForm()
    },
    async saveEdit() {
      try {
        // 懒上传：仅保存时才真正上传 SKU 图片
        const uploaded = await this.$refs.skuImageUpload.ensureUploaded()
        if (uploaded) {
          this.editForm.image = uploaded.url
        }
        const payload = { ...this.editForm }
        delete payload.specValueIds
        await updateSku(this.productId, this.skuId, payload)
        await this.syncSkuSpecs(this.skuId, this.editForm.specValueIds)
        this.closeEdit()
        await this.loadSku()
        alert('更新成功')
      } catch (error) {
        const msg = error.response?.data?.message || '保存失败，请稍后重试'
        alert(msg)
      }
    },
    // 同步 SKU 的规格值关联：只处理新旧差异，已存在且未变动的不动
    async syncSkuSpecs(skuId, valueIds) {
      const res = await getSkuSpecs(skuId)
      const list = (res && res.data && res.data.list) || []
      const newIds = (valueIds || []).map(Number)

      // 旧记录：spec_value_id -> 关联记录 id
      const oldMap = {}
      list.forEach(it => { oldMap[Number(it.spec_value_id)] = it.id })

      // 只在旧集合里、但不在新选择里的 -> 删除
      const toDelete = list.filter(it => !newIds.includes(Number(it.spec_value_id)))
      // 只在新选择里、但旧记录里没有的 -> 新增
      const toAdd = newIds.filter(id => !(id in oldMap))

      await Promise.all(toDelete.map(it => deleteSkuSpec(it.id)))
      await Promise.all(toAdd.map(id => createSkuSpec(skuId, id)))
    },
    formatYuan(fen) {
      const val = Number(fen || 0)
      return '' + (val / 100).toFixed(2)
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.sku-detail-page {
  padding: 20px;
}

.header {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
}

.header h2 {
  margin: 0;
  font-size: 20px;
  color: #333;
  flex: 1;
}

.back-btn {
  padding: 8px 16px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.back-btn:hover {
  background: #f5f5f5;
}

.edit-btn {
  padding: 8px 16px;
  background: #3498db;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.edit-btn:hover {
  background: #2980b9;
}

.loading-text {
  padding: 40px 0;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.info-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.card-header {
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
  background: #f8f9fa;
}

.card-header h3 {
  margin: 0;
  font-size: 16px;
  color: #333;
}

.card-body {
  padding: 20px;
}

.spec-card,
.product-card {
  margin-top: 20px;
}

.detail-top {
  display: flex;
  gap: 20px;
}

.detail-img {
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #eee;
  flex-shrink: 0;
}

.detail-img.no-image {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 13px;
  background: #fafafa;
}

.info-grid {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
}

.info-item {
  display: flex;
  flex-direction: column;
  padding: 12px 16px;
  background: #fafafa;
  border-radius: 6px;
}

.info-item label {
  font-size: 13px;
  color: #999;
  margin-bottom: 5px;
}

.info-item span {
  font-size: 14px;
  color: #333;
}

.spec-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.spec-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  background: #fafafa;
  border-radius: 6px;
}

.spec-name {
  font-size: 13px;
  color: #999;
  min-width: 100px;
}

.spec-value {
  font-size: 14px;
  color: #333;
  font-weight: 600;
}

.empty-text {
  padding: 30px 0;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.status {
  align-self: flex-start;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
}

.status.is-default {
  background: #fff3cd;
  color: #856404;
}

.status.is-on {
  background: #e8f5e9;
  color: #2ecc71;
}

.status.is-off {
  background: #ffebee;
  color: #e74c3c;
}

/* 编辑弹窗 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: #fff;
  border-radius: 8px;
  width: 560px;
  max-width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  font-size: 16px;
}

.close-btn {
  font-size: 24px;
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  color: #333;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-row {
  display: flex;
  gap: 15px;
}

.form-group-half {
  flex: 1;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px 20px;
  border-top: 1px solid #eee;
}

.btn-cancel {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}

.btn-save {
  padding: 8px 16px;
  background: #3498db;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
</style>
